import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { ChevronDown, RidgeLoader } from "../icons";
import { EASE_IN_OUT, EASE_OUT, heroOpen, loaderFinish, modalExit, reducedMotion, useRankShift } from "./motion";
import { appHref, GL_MODE, GL_PREFIX } from "../league";

/* Small pieces every sport's pages share. */

/**
 * The app's one loading indicator: the logo's ridge drawing itself in, the
 * flag going up, and what it is waiting for.
 *
 * One component rather than five copies of "Loading…", because the waits here
 * are not alike and the difference is worth saying. A warm box score is gone
 * before it paints; a cold player profile is four upstream calls behind a
 * three-second courtesy interval and can sit here for ten seconds. A reader
 * looking at a still panel for that long needs to know it is working and what
 * on — hence the label, which every caller supplies.
 *
 * role="status" makes it a polite live region, so the label is announced when
 * it appears rather than only being found by someone already exploring the
 * panel.
 */
export function Loading({ label, plate = false }: {
  label: string;
  /** Sits on a small plate in the middle of a skeleton (DSPRT-201) rather than on its own line. */
  plate?: boolean;
}) {
  const ref = useRef<HTMLParagraphElement>(null);
  // When the wait ends, the loader finishes its climb instead of vanishing
  // mid-loop: see loaderFinish. The cleanup runs while it is still on the page.
  useLayoutEffect(() => {
    const el = ref.current;
    const since = performance.now();
    return () => loaderFinish(el, since);
  }, []);
  return (
    <p ref={ref} className={`loading${plate ? " loading--plate" : ""}`} role="status">
      <RidgeLoader />
      <span>{label}</span>
    </p>
  );
}

const DIGITS = Array.from({ length: 10 }, (_, d) => <span key={d}>{d}</span>);

/**
 * A whole number that rolls to its new value like an odometer (DSPRT-201),
 * for live scores and percentages. Each digit is a column of 0-9 moved by a
 * transform, keyed by its place from the right, so a units digit that changes
 * rolls while the others stay put. Anything that isn't plain digits (a dash,
 * a null) renders as text. Screen readers get the number once, as text.
 */
/**
 * A segmented switch (.seg) for the keyboard (DSPRT-358): a set of radio
 * buttons, which is what it is. Tab stops on the switch once, at its chosen
 * segment, and the arrow keys move along it choosing as they go; Home and End
 * take the ends. It used to be a stop a segment, with the arrows doing
 * nothing. Spread `segGroup(label)` on the track and `segOption(on)` on each
 * segment; the segment keeps its own onClick, which an arrow press calls.
 */
export function segGroup(label: string) {
  return { role: "radiogroup" as const, "aria-label": label, onKeyDown: segKeyDown };
}

export function segOption(on: boolean) {
  return { role: "radio" as const, "aria-checked": on, tabIndex: on ? 0 : -1 };
}

function segKeyDown(e: React.KeyboardEvent<HTMLElement>) {
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  const step = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
  if (!step && e.key !== "Home" && e.key !== "End") return;
  const segs = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>(".seg__btn:not(:disabled)"));
  const at = segs.indexOf(document.activeElement as HTMLButtonElement);
  if (at < 0 || segs.length < 2) return;
  // Also keeps the arrows from the page's own shortcuts (the day before and after on Scores).
  e.preventDefault();
  const to = e.key === "Home" ? 0 : e.key === "End" ? segs.length - 1 : (at + step + segs.length) % segs.length;
  segs[to].focus();
  segs[to].click();
}

export function RollingNumber({ value }: { value: number | string | null | undefined }) {
  const s = value == null ? "" : String(value);
  if (!/^\d{1,4}$/.test(s)) return <>{s}</>;
  return (
    <span className="roll">
      <span className="sr-only">{s}</span>
      {[...s].map((d, i) => (
        <span key={s.length - i} className="roll__c" aria-hidden="true">
          <span className="roll__s" style={{ transform: `translateY(${-Number(d) * 10}%)` }}>{DIGITS}</span>
        </span>
      ))}
    </span>
  );
}

/**
 * Text whose numbers roll (DSPRT-203): "in 16h 05m" with each number a
 * RollingNumber, so a countdown turning over rolls its digits in place.
 */
export function RollingText({ text }: { text: string }) {
  const parts = text.split(/(\d+)/);
  return <>{parts.map((p, i) => (/^\d+$/.test(p) ? <RollingNumber key={i} value={p} /> : p))}</>;
}

/** Totals that have already counted up this visit, so going back to a page doesn't replay them. */
const counted = new Set<string>();

/**
 * A big total that counts up from zero the first time it scrolls into view
 * (DSPRT-203), quick at first and settling on the real figure, keeping its
 * thousands separators and decimals. Anything that isn't a plain number is
 * shown as it is. `id` names the figure, so it counts once per visit.
 */
export function CountUp({ text, id }: { text: string; id: string }) {
  const m = /^(\d{1,3}(?:,\d{3})+|\d+)(\.\d+)?$/.exec(text);
  const target = m ? Number(text.replace(/,/g, "")) : NaN;
  const decimals = m?.[2] ? m[2].length - 1 : 0;
  const grouped = !!m && m[1].includes(",");
  const key = `${id}:${text}`;
  const eligible = !!m && target > 0 && !counted.has(key) && !reducedMotion();
  const [shown, setShown] = useState<string | null>(() => (eligible ? "0" : null));
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !eligible) { setShown(null); return; }
    const fmt = (v: number) => (grouped ? Math.round(v).toLocaleString("en-US") : v.toFixed(decimals));
    let raf = 0;
    const io = new IntersectionObserver((es) => {
      if (!es.some((e) => e.isIntersecting)) return;
      io.disconnect();
      counted.add(key);
      const t0 = performance.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - t0) / 1200);
        const e = 1 - Math.pow(1 - t, 3);
        if (t < 1) { setShown(fmt(target * e)); raf = requestAnimationFrame(step); }
        else setShown(null);
      };
      raf = requestAnimationFrame(step);
    });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (shown == null) return <>{text}</>;
  return (
    <span ref={ref} className="countup">
      <span className="sr-only">{text}</span>
      <span aria-hidden="true">{shown}</span>
    </span>
  );
}

/**
 * A profile section that folds down to its heading (DSPRT-144).
 *
 * Wraps a whole `.tm__sec` rather than rebuilding it, so every section keeps
 * its own heading markup — a count badge, a chart picker, a List/Depth chart
 * toggle — and folding just hides what comes after that heading (CSS, see
 * .fold). The content stays mounted, so a picked chart stat or an open
 * toggle survives a fold and unfold.
 *
 * Every profile opens with every section open (DSPRT-153): folding one
 * player's Last Games says nothing about the next player's, so the state is
 * this card's alone and `resetKey` (the player or club) opens it again.
 */
export function Fold({ label, resetKey, summary, children }: {
  /** Kept for callers that name the section; no longer stored anywhere. */
  name?: string;
  label: string;
  resetKey?: string | null;
  /**
   * One line shown beside the heading while folded, so a folded section
   * still says what it holds — "POR won · ahead 86% of the game" (DSPRT-212).
   */
  summary?: string | null;
  children: React.ReactNode;
}) {
  const [folded, setFolded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // The animations of a fold in motion. A closing one is held at its last
  // frame until React has the section folded; the layout effect below drops
  // them once that is on the page, so there is no frame at full height.
  const anims = useRef<Animation[]>([]);
  const clear = () => {
    anims.current.forEach((a) => a.cancel());
    anims.current = [];
    if (ref.current) ref.current.style.overflow = "";
  };
  useLayoutEffect(clear, [folded]);
  useEffect(() => { clear(); setFolded(false); }, [resetKey]);

  /**
   * Folding and unfolding animate the section's height between the full card
   * and the bare heading, with the content fading under it. The content is
   * still display: none once folded (see .fold), so it can't be tabbed into;
   * it is only kept on screen for the length of the closing animation.
   */
  const toggle = () => {
    const el = ref.current;
    // A hidden page runs no animation frames, so onfinish would never come.
    if (!el || reducedMotion() || document.visibilityState !== "visible") { setFolded((f) => !f); return; }
    if (anims.current.length) return; // still moving: ignore the click
    const closing = !folded;
    const from = el.offsetHeight;
    let to: number;
    if (closing) {
      // Measure the folded height without painting it.
      el.classList.add("is-folded");
      to = el.offsetHeight;
      el.classList.remove("is-folded");
    } else {
      flushSync(() => setFolded(false));
      to = el.offsetHeight;
    }
    if (from === to) { setFolded(closing); return; }
    const content = Array.from(el.querySelectorAll<HTMLElement>(".tm__sec > :not(.tm__h3)"));
    const opts: KeyframeAnimationOptions = { duration: closing ? 200 : 240, easing: closing ? EASE_IN_OUT : EASE_OUT, fill: "forwards" };
    el.style.overflow = "hidden";
    if (closing) el.classList.add("is-closing"); // turns the chevron at once
    const box = el.animate([{ height: `${from}px` }, { height: `${to}px` }], opts);
    const fade = content.map((c) => c.animate(
      closing ? [{ opacity: 1 }, { opacity: 0 }] : [{ opacity: 0 }, { opacity: 1 }],
      closing ? { ...opts, duration: 120 } : { ...opts, delay: 40, duration: 180 }));
    anims.current = [box, ...fade];
    if (closing) box.onfinish = () => setFolded(true);
    else box.onfinish = clear;
  };

  return (
    <div ref={ref} className={`fold ${folded ? "is-folded" : ""}`}>
      {children}
      {folded && summary && <span className="fold__summary">{summary}</span>}
      <button type="button" className="fold__btn" onClick={toggle} aria-expanded={!folded}
              aria-label={`${folded ? "Expand" : "Collapse"} ${label}`} title={folded ? "Expand" : "Collapse"}>
        <ChevronDown />
      </button>
    </div>
  );
}

/**
 * Opens a <dialog> modally and locks the page behind it.
 *
 * Shared by both overlays. showModal() supplies the focus trap, the inert
 * background, the top layer and Escape — all the things the previous
 * hand-rolled div claimed via ARIA and did not actually do. The one thing it
 * does not do is stop the page behind from scrolling on a wheel gesture, which
 * is what the body lock is for.
 */
/**
 * True while the browser is laying the page out for print (DSPRT-307), for
 * the few places that show one thing at a time on screen and should print
 * all of it (a box score's two teams). Flushed synchronously: the snapshot is
 * taken as soon as the beforeprint handlers return.
 */
export function usePrinting(): boolean {
  const [printing, setPrinting] = useState(false);
  useEffect(() => {
    const on = () => flushSync(() => setPrinting(true));
    const off = () => setPrinting(false);
    window.addEventListener("beforeprint", on);
    window.addEventListener("afterprint", off);
    return () => {
      window.removeEventListener("beforeprint", on);
      window.removeEventListener("afterprint", off);
    };
  }, []);
  return printing;
}

/** Where the dialog that just closed was sending focus, for one opened from it (B-035). */
let handoff: HTMLElement | null = null;
/** Each closed dialog's opener, for a dialog opened from inside it (B-035). */
const forwardTo = new WeakMap<HTMLDialogElement, HTMLElement | null>();

export function useModalDialog<T extends HTMLDialogElement>(): React.RefObject<T> {
  const ref = useRef<T>(null);

  // Closing animates out (DSPRT-201): a copy of the panel fades or slides away
  // after React has removed the real one. This cleanup runs before that
  // removal, while the panel is still there to copy.
  useLayoutEffect(() => {
    const el = ref.current;
    return () => modalExit(el);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || el.open) return;
    // Whatever opened it — a row, a name — gets focus back on close
    // (DSPRT-227): the dialog is removed by React rather than closed by the
    // browser, so keyboard users were left on <body>, back at the top.
    // A dialog opened from another as it closed (a player picked in search,
    // B-035) inherits where that one would have sent focus: its own opener is
    // gone with it.
    // Never something inside this dialog: an effect run twice (React's strict
    // mode in development) found focus already in it the second time.
    const active = document.activeElement;
    const opener = active instanceof HTMLElement && active !== document.body && !el.contains(active) ? active : handoff;
    const from = opener?.closest("dialog") ?? null;
    el.showModal();
    // showModal() moves focus to the first focusable child and scrolls it into
    // view, which lands the view ~30px down — far enough that the first row of
    // content sits behind the sticky header and simply isn't there. The stat
    // tiles lost their labels to exactly this. Both boxes are reset because
    // the panel is the scroller and the dialog can still be nudged.
    el.scrollTop = 0;
    el.querySelector<HTMLElement>(".modal__panel")?.scrollTo(0, 0);
    // showModal() leaves the keyboard on the dialog's first focusable child,
    // which on a card with a crowded header is whatever happens to come first
    // — the Born in USA flag, a link out of the card you just opened (B-221).
    // data-open-focus="close" says the dialog opens on its Close control
    // instead, as the box score card does by having Close first. Dialogs that
    // open on a control of their own (search on its box, Share on Copy) say
    // nothing and keep it.
    if (el.dataset.openFocus === "close") el.querySelector<HTMLElement>(".modal__close")?.focus({ preventScroll: true });
    // Opened from a headshot or crest: that image flies into the header (DSPRT-201).
    heroOpen(el);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
      if (el.open) el.close();
      // Read by a dialog opening in this same commit, before the frame below,
      // or later by one opened from inside this dialog while it was still up.
      handoff = opener;
      forwardTo.set(el, opener);
      // After React has finished removing it, and only if nothing else took focus meanwhile.
      requestAnimationFrame(() => {
        if (handoff === opener) handoff = null;
        const lost = !document.activeElement || document.activeElement === document.body;
        if (!lost) return;
        // Opened from inside a dialog that has closed since (a search result):
        // where that dialog would have sent focus, the search button.
        let back = opener, via = from;
        for (let hops = 0; hops < 4 && back && !back.isConnected && via && forwardTo.has(via); hops++) {
          const next: HTMLElement | null = forwardTo.get(via) ?? null;
          via = next?.closest("dialog") ?? null;
          back = next;
        }
        if (back?.isConnected) back.focus({ preventScroll: true });
        // Opened by clicking something that takes no focus of its own — a
        // game card on Scores is an <article>, clickable in full (B-211) —
        // so there is no opener to hand the keyboard back to. The page's own
        // <main> takes it, so Tab carries on from the content rather than
        // restarting at "Skip to content".
        else document.getElementById("main")?.focus({ preventScroll: true });
      });
    };
  }, []);

  return ref;
}

/* ---------------- portraits ---------------- */

/**
 * Same combiner trick as sizedCrest, against the headshot path rather than the
 * team-logo one. Worth more here than there: a full headshot is ~270KB and the
 * resized one is ~40KB, for an image that renders under 100px wide.
 *
 * Matched strictly, so a URL from anywhere but that host and path shape passes
 * through untouched rather than being rewritten into a broken request.
 */
const ESPN_HEADSHOT = /^https:\/\/a\.espncdn\.com(\/i\/headshots\/[\w./-]+\.png)$/;

/** ESPN's headshots are 1040x760 — roughly 1.38:1, which this preserves. */
export const HEADSHOT_RATIO = 1040 / 760;

export function sizedHeadshot(url: string | null | undefined, px: number): string | null {
  if (!url) return null;
  const m = ESPN_HEADSHOT.exec(url);
  if (!m) return url;
  const w = Math.round(px * 2); // 2x, same reason the img carries width/height
  return `https://a.espncdn.com/combiner/i?img=${m[1]}&w=${w}&h=${Math.round(w / HEADSHOT_RATIO)}`;
}

/**
 * A head-and-shoulders silhouette in ESPN's 104:76 portrait frame, head near
 * the top and shoulders flush with the bottom edge, so it lands where a real
 * cut-out portrait would. Traced from the reference image the user supplied
 * (DSPRT-63) rather than embedded as one: a black-on-white PNG could not take
 * the theme's colour, and would sit as a white box on the dark theme.
 */
export const SILHOUETTE =
  "M50.6 4C49.2 4.3 46.1 5.3 44.5 5.9C42.9 6.5 41.6 7 40.9 7.4C40.3 7.8 40.5 7.8 40.4 8.2C40.3 8.6 40.5 9.2 40.2 9.7C39.8 10.2 38.8 10.7 38.4 11.2C38 11.8 37.9 11.2 37.6 13C37.4 14.9 36.8 20.1 36.9 22.2C36.9 24.3 38.1 24.8 38.1 25.5C38.1 26.2 37 25.5 36.9 26.5C36.7 27.6 37.1 30.6 37.4 31.9C37.6 33.1 37.7 33.1 38.4 33.9C39.1 34.7 40.9 35.6 41.4 36.4C42 37.3 41.4 38.1 41.7 39C42 39.9 42.8 40.8 43 41.8C43.2 42.8 43.2 44.2 43 45.1C42.7 45.9 41.9 46.2 41.4 46.9C41 47.5 40.8 48.3 40.4 48.9C40 49.5 40.5 49.1 38.9 50.2C37.3 51.2 33.3 53.8 30.8 55.3C28.2 56.8 25.4 58 23.6 59.1C21.8 60.2 20.9 61.1 20.1 61.9C19.3 62.6 19.1 62.9 18.8 63.7C18.5 64.4 18.2 64.1 18 66.2L18 76L86 76L86 66C85.9 64 85.8 64.8 85.5 64.2C85.2 63.5 84.8 62.6 84.2 61.9C83.6 61.2 85 61.7 81.9 59.8C78.8 58 68.7 52.5 65.6 50.7C62.6 48.9 64.3 49.8 63.6 48.9C62.8 48 61.5 46.3 61 45.1C60.6 43.9 60.8 42.8 61 41.8C61.2 40.8 62 40 62.3 39C62.6 38 62.7 36.3 63.1 35.7C63.5 35 64.1 35.2 64.6 34.9C65.1 34.6 65.5 34.3 65.9 33.6C66.3 33 66.7 31.9 66.9 31.1C67 30.3 66.8 29.7 66.9 29.1C67 28.4 67.3 27.5 67.4 27C67.4 26.6 67.4 26.5 67.1 26.3C66.9 26 66.2 26.2 66.1 25.5C66.1 24.8 66.8 23.2 66.9 22.2C67 21.1 67.2 20.9 66.9 19.1C66.5 17.4 65.5 13.6 64.8 11.8C64.2 9.9 64.1 9.4 62.8 8.2C61.5 7 58.7 5.3 57.2 4.6C55.7 4 54.3 4.5 53.6 4.4C53 4.3 53.6 4.1 53.1 4Z";

/* ---------------- not found ---------------- */

/**
 * What an address that leads nowhere shows, in place of quietly landing on the
 * home page (DSPRT-227): a mistyped or stale link said nothing about why the
 * page you asked for wasn't there. `what` names the thing ("player", "page").
 */
/**
 * A link inside the app (DSPRT-291): a real address, so a new tab, "copy link
 * address" and a crawler all get the page, while the plain left click is
 * handed to the app, which opens the same thing without a page load.
 */
export function AppLink({ href, onOpen, children, ...rest }: {
  href: string;
  onOpen: () => void;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "onClick">) {
  return (
    <a
      {...rest}
      // Spelled for the league shown (DSPRT-474): "/team/OSC" is "/gleague/team/OSC" on the G League side.
      href={appHref(href)}
      onClick={(e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        // On the G League desk a page only the NBA has (a country, the free agents)
        // is the NBA desk's: the browser loads it (B-128), the in-app step would land on Page not found.
        if (GL_MODE && !appHref(href).startsWith(GL_PREFIX)) return;
        e.preventDefault();
        onOpen();
      }}
    >
      {children}
    </a>
  );
}

export function NotFound({ what, path, links }: { what: string; path: string; links: { href: string; label: string }[] }) {
  return (
    <div className="notfound">
      <p className="notfound__lead">There's no {what} at <code className="notfound__path">{path}</code>.</p>
      <p className="notfound__hint">It may be an old link, or a typo in the address.</p>
      <nav className="notfound__links" aria-label="Where to go instead">
        {links.map((l) => <a key={l.href} className="notfound__link" href={l.href}>{l.label}</a>)}
      </nav>
    </div>
  );
}

/**
 * A leader board's list (.ldr__list), whose players slide to their new
 * places when the board changes under the reader: another season, totals
 * instead of averages (DSPRT-611). `board` names it across loads; `order` is
 * what it ranks now. Its rows carry data-rank.
 */
export function LeaderList({ board, order, className = "ldr__list rise", children, ...rest }: {
  board: string; order: string; className?: string; children: React.ReactNode;
} & Omit<React.OlHTMLAttributes<HTMLOListElement>, "className" | "children">) {
  const ref = useRankShift<HTMLOListElement>(board, order);
  return <ol {...rest} className={className} ref={ref}>{children}</ol>;
}
